import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Pagination from "../component/Pagination";
import { toast } from "react-toastify";

import {
  fetchSizes,
  createSize,
  updateSize,
  deleteSize,
  toggleSizeStatus
} from "../../../slice/size/sizeSlice";

export default function Size() {
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const limit = 10;

  const { sizes = [], loading, pagination = {} } = useSelector(
    (state) => state.size
  );

  /* ================= LOCAL STATE ================= */
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [sizeWidth, setSizeWidth] = useState("");
  const [sizeHeight, setSizeHeight] = useState("");
  const [selectedSize, setSelectedSize] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [errors, setErrors] = useState({ width: "", height: "" });

  /* ================= FETCH ================= */
  useEffect(() => {
    dispatch(fetchSizes({ page, limit }));
  }, [dispatch, page]);

  /* ================= ADD ================= */
  const handleCreate = () => {
    const newErrors = { width: "", height: "" };
    
    if (!sizeWidth.trim()) {
      newErrors.width = "Width is required";
    }
    if (!sizeHeight.trim()) {
      newErrors.height = "Height is required";
    }

    setErrors(newErrors);

    if (newErrors.width || newErrors.height) {
      return;
    }

    const sizeName = `${sizeWidth.trim()}x${sizeHeight.trim()}`;
    
    dispatch(createSize({ name: sizeName }))
      .unwrap()
      .then(() => {
        toast.success("Size added successfully");
        closeAllModals();
        dispatch(fetchSizes({ page, limit }));
      });
  };

  /* ================= UPDATE ================= */
  const handleUpdate = () => {
    const newErrors = { width: "", height: "" };
    
    if (!sizeWidth.trim()) {
      newErrors.width = "Width is required";
    }
    if (!sizeHeight.trim()) {
      newErrors.height = "Height is required";
    }

    setErrors(newErrors);

    if (newErrors.width || newErrors.height || !selectedSize) {
      return;
    }

    const sizeName = `${sizeWidth.trim()}x${sizeHeight.trim()}`;

    dispatch(updateSize({ id: selectedSize._id, name: sizeName }))
      .unwrap()
      .then(() => {
        toast.success("Size updated successfully");
        closeAllModals();
        dispatch(fetchSizes({ page, limit }));
      });
  };

  /* ================= DELETE ================= */
  const confirmDelete = () => {
    dispatch(deleteSize(deleteId))
      .unwrap()
      .then(() => {
        toast.success("Size deleted successfully");
        closeAllModals();
        dispatch(fetchSizes({ page, limit }));
      });
  };

  /* ================= HELPERS ================= */
  const closeAllModals = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setSelectedSize(null);
    setDeleteId(null);
    setSizeWidth("");
    setSizeHeight("");
    setErrors({ width: "", height: "" });
  };

  // Helper function to parse size name (e.g., "6x8" -> width: "6", height: "8")
  const parseSizeName = (name) => {
    const parts = name.split('x');
    return {
      width: parts[0] || "",
      height: parts[1] || ""
    };
  };

  return (
    <div className="row">
      <div className="card mt-3">

        {/* HEADER */}
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="card-title">Size List</h4>
          <button
            className="btn cusBTNPop btn-sm"
            onClick={() => {
              setSizeWidth("");
              setSizeHeight("");
              setSelectedSize(null);
              setShowAddModal(true);
            }}
          >
            + Add Size
          </button>
        </div>

        {/* TABLE */}
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="bg-light">
              <tr>
                <th>SI</th>
                <th>Size</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="4" className="text-center">Loading...</td>
                </tr>
              ) : sizes.length ? (
                sizes.map((size, index) => (
                  <tr key={size._id}>
                    <td>{index + 1}</td>
                    <td>{size.name}</td>

                    {/* STATUS */}
                    <td>
                      <div
                        className={`status-toggle ${size.status ? "enabled" : "disabled"}`}
                        onClick={() => dispatch(toggleSizeStatus(size._id))}
                      >
                        <span className="toggle-knob"></span>
                        <span className="toggle-text">
                          {size.status ? "Enabled" : "Disabled"}
                        </span>
                      </div>
                    </td>

                    {/* ACTIONS */}
                    <td className="d-flex gap-2">
                    

                           <button
                          className="btn btn-soft-primary btn-sm"
                         onClick={() => {
                            setSelectedSize(size);
                            const parsed = parseSizeName(size.name);
                            setSizeWidth(parsed.width);
                            setSizeHeight(parsed.height);
                            setShowEditModal(true);
                          }}
                        >
                          <iconify-icon icon="solar:pen-2-broken" />
                        </button>

                        <button
                          className="btn btn-soft-danger btn-sm"
                          onClick={() => {
                            setDeleteId(size._id);
                            setShowDeleteModal(true);
                          }}
                        >
                          <iconify-icon icon="solar:trash-bin-minimalistic-2-broken" />
                        </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center text-muted">
                    No sizes found
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="card-footer border-top">
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={setPage}
            />
          </div>
        </div>

        {/* ADD MODAL */}
        {showAddModal && (
          <div className="modal-overlay" onClick={closeAllModals}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h5>Add Size</h5>
              </div>
              <div className="modal-body">
                <div className="d-flex gap-2 align-items-center">
                  <div className="flex-fill">
                    <input
                      className={`form-control ${errors.width ? 'is-invalid' : ''}`}
                      placeholder="Width (e.g., 6)"
                      value={sizeWidth}
                      onChange={(e) => {
                        setSizeWidth(e.target.value);
                        setErrors(prev => ({ ...prev, width: "" }));
                      }}
                    />
                    {errors.width && (
                      <div className="invalid-feedback d-block">
                        {errors.width}
                      </div>
                    )}
                  </div>
                  <span className="fw-bold">×</span>
                  <div className="flex-fill">
                    <input
                      className={`form-control ${errors.height ? 'is-invalid' : ''}`}
                      placeholder="Height (e.g., 8)"
                      value={sizeHeight}
                      onChange={(e) => {
                        setSizeHeight(e.target.value);
                        setErrors(prev => ({ ...prev, height: "" }));
                      }}
                    />
                    {errors.height && (
                      <div className="invalid-feedback d-block">
                        {errors.height}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn cuscancel" onClick={closeAllModals}>
                  Cancel
                </button>
                <button className="btn cusBTNPop" onClick={handleCreate}>
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

        {/* EDIT MODAL */}
        {showEditModal && (
          <div className="modal-overlay" onClick={closeAllModals}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h5>Edit Size</h5>
              </div>
              <div className="modal-body">
                <div className="d-flex gap-2 align-items-center">
                  <div className="flex-fill">
                    <input
                      className={`form-control ${errors.width ? 'is-invalid' : ''}`}
                      placeholder="Width"
                      value={sizeWidth}
                      onChange={(e) => {
                        setSizeWidth(e.target.value);
                        setErrors(prev => ({ ...prev, width: "" }));
                      }}
                    />
                    {errors.width && (
                      <div className="invalid-feedback d-block">
                        {errors.width}
                      </div>
                    )}
                  </div>
                  <span className="fw-bold">×</span>
                  <div className="flex-fill">
                    <input
                      className={`form-control ${errors.height ? 'is-invalid' : ''}`}
                      placeholder="Height"
                      value={sizeHeight}
                      onChange={(e) => {
                        setSizeHeight(e.target.value);
                        setErrors(prev => ({ ...prev, height: "" }));
                      }}
                    />
                    {errors.height && (
                      <div className="invalid-feedback d-block">
                        {errors.height}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn cuscancel" onClick={closeAllModals}>
                  Cancel
                </button>
                <button className="btn cusBTNPop" onClick={handleUpdate}>
                  Update
                </button>
              </div>
            </div>
          </div>
        )}

        {/* DELETE MODAL */}
        {showDeleteModal && (
          <div className="modal-overlay" onClick={closeAllModals}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-body text-center">
                Are you sure you want to delete this size?
              </div>
              <div className="modal-footer">
                <button className="btn cusClose" onClick={closeAllModals}>
                  Cancel
                </button>
                <button className="btn cuscancel" onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}